<template>
  <div>
    <div class="headbar">
      <div class="headbar__left">
        <button class="navbar__toggler">
          <span></span>
          <span></span>
          <span></span>
        </button>
        <button
          class="btn-reset logo-btn"
          @click="$router.push({ name: 'Dashboard' }).catch((err) => {})"
        >
          <img
            class="logo"
            src="/img/logo.png"
            alt="scando.ch Logo"
            width="105"
          />
        </button>
        <div id="headbar__title" class="headbar__title">
          {{ title }}
        </div>
      </div>

      <div class="headbar__right">
        <notifications
          :data-endpoint="notificationsEndpoint"
          :persistent-notifications="$store.state.notifications.stack"
          :new-handler="handleNewNotifications"
          :icon-src="icon"
          :base-url="baseUrl"
          @closed-unread="$store.dispatch('notifications/read')"
          @removed="$store.dispatch('notifications/delete', $event.id)"
          @removed-all="$store.dispatch('notifications/clear')"
          @action="handleNotificationAction"
        >
        </notifications>

        <button
          v-for="(button, index) in additionalButtons"
          :key="index"
          @click="button.handler"
        >
          <template v-if="button.label">{{ button.label }}</template>
          <img v-else-if="button.icon" :src="icon(button.icon)" />
        </button>

        <router-link to="/calendar">
          <img :src="icon('planning.svg')" />
        </router-link>

        <router-link to="/profile">
          <img
            :src="$store.getters['user/profilePicture']"
            width="50"
            height="50"
          />
        </router-link>
      </div>
    </div>

    <nav class="navbar">
      <div class="navbar__head">
        <!-- <img
          class="logo"
          src="/img/logo.png"
          alt="Smartdoors Logo"
          width="100"
        /> -->
        <button class="navbar__toggler" type="button">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      <ul class="navbar__nav-items list-reset">
        <router-link
          to="/"
          tag="li"
          class="navbar__nav-item"
          v-slot="{ href, route, navigate, isExactActive }"
        >
          <li :class="{ active: isExactActive }">
            <a :href="href" @click="navigate">
              <span class="navbar__nav-item__icon"
                ><img :src="icon('home.svg')"
              /></span>
              <span class="navbar__nav-item__text">{{
                __("main.nav.overview")
              }}</span>
            </a>
          </li>
        </router-link>
        <router-link
          to="/calendar"
          tag="li"
          class="navbar__nav-item"
          v-slot="{ href, route, navigate, isExactActive }"
        >
          <li :class="{ active: isExactActive }">
            <a :href="href" @click="navigate">
              <span class="navbar__nav-item__icon"
                ><img :src="icon('planning.svg')"
              /></span>
              <span class="navbar__nav-item__text">Kalender</span>
            </a>
          </li>
        </router-link>
        <router-link
          to="/profile"
          tag="li"
          class="navbar__nav-item"
          v-slot="{ href, route, navigate, isExactActive }"
        >
          <li :class="{ active: isExactActive }">
            <a :href="href" @click="navigate">
              <span class="navbar__nav-item__icon"
                ><img :src="icon('user.svg')"
              /></span>
              <span class="navbar__nav-item__text">{{
                __("main.nav.profile")
              }}</span>
            </a>
          </li>
        </router-link>
        <li class="navbar__nav-item">
          <button @click="logout">
            <span class="navbar__nav-item__icon"
              ><img :src="icon('logout.svg')"
            /></span>
            <span class="navbar__nav-item__text">{{
              __("main.nav.logout")
            }}</span>
          </button>
        </li>
      </ul>
    </nav>

    <div class="main">
      <slot />
    </div>

    <windows ref="windows"></windows>
    <popups ref="popups"></popups>
  </div>
</template>

<script>
import BaseLayout from "./BaseLayout";
import navbar from "smartdoors-shared/js/modules/navbar";
import HasWindows from "smartdoors-shared/js/components/Mixins/HasWindows";
import {
  handleAction as notificationActionHandler,
  handleNew as notificationsNewHandler,
} from "../helper/notifications";

export default {
  extends: BaseLayout,
  mixins: [HasWindows],
  props: {
    title: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      additionalButtons: [],
      baseUrl: window.location.origin,
    };
  },
  created() {
    this.newNotificationsHandledResolver = [];

    this.notificationsLoadedAndHandled = this.newNotificationsHandled();
    this.notificationsLoaded = this.loadNotifications();
  },
  mounted() {
    this.windows = this.$refs.windows;
    this.popups = this.$refs.popups;
    this.filesUrlPrefix = this.env.PORTAL_URL;

    navbar(
      document.querySelector(".navbar"),
      0,
      document.querySelectorAll(".navbar__toggler, .navbar .navbar__nav-item")
    );

    this.$emit("mounted", this);
  },
  watch: {
    $route(to, from) {
      this.additionalButtons = [];
    },
  },
  computed: {
    notificationsEndpoint() {
      let endpoint = this.env.NOTIFICATIONS_ENDPOINT;

      endpoint = endpoint.replace("%user_id%", this.$store.state.user.data.id);
      endpoint = endpoint.replace(
        "%user_hash%",
        encodeURIComponent(this.$store.state.user.notificationsHash)
      );

      return endpoint;
    },
  },
  methods: {
    logout() {
      this.$store.commit("user/logout");
      this.$router.push("/login");
    },
    async loadNotifications() {
      await this.$store.restored;
      const notifications = await this.$store.dispatch("notifications/load");

      if (!notifications || !notifications.length) {
        this.resolveNewNotificationsHandled([]);
      }

      return notifications;
    },
    handleNotificationAction({ notification, actionKey, actionData }) {
      notificationActionHandler.call(this, notification, actionKey, actionData);
    },
    newNotificationsHandled() {
      return new Promise((resolve) => {
        this.newNotificationsHandledResolver.push(resolve);
      });
    },
    resolveNewNotificationsHandled(newNotifications) {
      for (const resolver of this.newNotificationsHandledResolver) {
        resolver(newNotifications);
      }

      this.newNotificationsHandledResolver = [];
    },
    async handleNewNotifications(newNotifications, source) {
      await notificationsNewHandler.call(this, newNotifications, source);

      this.resolveNewNotificationsHandled(newNotifications);
    },
    addButton(button) {
      this.additionalButtons.push(button);
    },
  },
};
</script>